import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal, X, GitCompare, LayoutGrid, Home, Building2, Trees, Clock } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Layout from "@/components/Layout";
import PropertyCard from "@/components/PropertyCard";
import { formatPrice, Property } from "@/data/properties";
import { useProperties } from "@/hooks/useProperties";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type SortKey = "recent" | "price_asc" | "price_desc" | "size_asc" | "size_desc";
type Categoria = "todo" | "casas" | "pisos" | "terrenos";

export default function Propiedades() {
  const { available: allProperties, loading } = useProperties();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [categoria, setCategoria] = useState<Categoria>("todo");
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const filtered = useMemo(() => {
    if (categoria === "pisos" || categoria === "terrenos") return [];
    let result = allProperties.filter((p) => {
      const q = search.toLowerCase();
      return (
        p.titulo.toLowerCase().includes(q) ||
        p.ubicacion.toLowerCase().includes(q) ||
        p.ciudad.toLowerCase().includes(q)
      );
    });
    switch (sort) {
      case "price_asc": result.sort((a, b) => a.precio - b.precio); break;
      case "price_desc": result.sort((a, b) => b.precio - a.precio); break;
      case "size_asc": result.sort((a, b) => a.metros_cuadrados - b.metros_cuadrados); break;
      case "size_desc": result.sort((a, b) => b.metros_cuadrados - a.metros_cuadrados); break;
    }
    return result;
  }, [allProperties, search, sort, categoria]);

  const toggleCompare = (id: string) => {
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const compareProperties = allProperties.filter((p) => compareIds.includes(p.id));

  return (
    <Layout>
      <Helmet>
        <title>Catálogo de propiedades en el Vallès Occidental | Sercom</title>
        <meta name="description" content="Explora todas las casas y villas disponibles de Sercom en el Vallès Occidental y provincia de Barcelona. Filtra por precio, tamaño y características." />
        <link rel="canonical" href="https://sercomcs.com/Propiedades" />
        <meta property="og:title" content="Catálogo de propiedades en el Vallès Occidental | Sercom" />
        <meta property="og:description" content="Todas las viviendas disponibles de Sercom en el Vallès Occidental y provincia de Barcelona. Filtra y compara." />
        <meta property="og:url" content="https://sercomcs.com/Propiedades" />
      </Helmet>
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-5xl font-bold mb-3"
        >
          Todas nuestras propiedades
        </motion.h1>
        <h2 className="text-base md:text-lg text-muted-foreground mb-8 font-normal">
          Listado completo de casas y villas disponibles en el Vallès Occidental y provincia de Barcelona
        </h2>

        {/* Category tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap gap-2 mb-6"
          role="tablist"
          aria-label="Categoría de propiedades"
        >
          {([
            { key: "todo", label: "Todo", Icon: LayoutGrid },
            { key: "casas", label: "Casas", Icon: Home },
            { key: "pisos", label: "Pisos", Icon: Building2 },
            { key: "terrenos", label: "Terrenos", Icon: Trees },
          ] as { key: Categoria; label: string; Icon: typeof Home }[]).map(({ key, label, Icon }) => {
            const active = categoria === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                onClick={() => setCategoria(key)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-foreground border-border hover:border-accent hover:text-accent"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border rounded-2xl p-4 mb-8 flex flex-col sm:flex-row gap-4"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="property-search"
              aria-label="Buscar propiedades por título, ubicación o ciudad"
              placeholder="Buscar por título, ubicación o ciudad..."
              className="pl-10 rounded-xl"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
            <SelectTrigger className="w-full sm:w-52 rounded-xl">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Más recientes</SelectItem>
              <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
              <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
              <SelectItem value="size_asc">Tamaño: menor a mayor</SelectItem>
              <SelectItem value="size_desc">Tamaño: mayor a menor</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <div key={p.id} className="relative">
                  <PropertyCard property={p} index={i} />
                  <div className="absolute top-3 right-3 z-10 flex gap-1.5">
                    <button
                      onClick={(e) => { e.preventDefault(); toggleCompare(p.id); }}
                      aria-label={compareIds.includes(p.id) ? `Quitar ${p.titulo} de la comparación` : `Añadir ${p.titulo} a la comparación`}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        compareIds.includes(p.id) ? "bg-accent text-accent-foreground" : "bg-white/80 text-muted-foreground hover:bg-white"
                      }`}
                      title="Comparar"
                    >
                      <GitCompare className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-muted-foreground text-sm mt-8">
              {filtered.length} propiedad{filtered.length !== 1 ? "es" : ""} encontrada{filtered.length !== 1 ? "s" : ""}
            </p>
          </>
        ) : categoria === "pisos" || categoria === "terrenos" ? (
          <div className="text-center py-20 bg-card border rounded-2xl">
            <div className="w-16 h-16 rounded-full bg-accent/10 text-accent flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8" />
            </div>
            <p className="text-xl font-semibold mb-1">
              {categoria === "pisos" ? "Pisos" : "Terrenos"}
            </p>
            <p className="text-muted-foreground">Próximamente</p>
          </div>
        ) : (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-muted-foreground/70 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">No se encontraron propiedades</p>
            <p className="text-muted-foreground text-sm mt-1">Prueba con otros términos de búsqueda</p>
          </div>
        )}
      </div>

      {/* Compare floating button */}
      {compareIds.length > 0 && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40"
        >
          <Button
            onClick={() => setCompareOpen(true)}
            className="rounded-full px-6 py-3 gap-2 shadow-xl bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <GitCompare className="w-4 h-4" />
            Comparar ({compareIds.length})
            <button
              onClick={(e) => { e.stopPropagation(); setCompareIds([]); }}
              aria-label="Vaciar lista de comparación"
              className="ml-2 hover:bg-black/10 rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </Button>
        </motion.div>
      )}

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Comparar propiedades</DialogTitle>
          </DialogHeader>
          {compareProperties.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Característica</th>
                    {compareProperties.map((p) => (
                      <th key={p.id} className="text-left py-3 px-2 font-semibold">{p.titulo}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {([
                    ["Precio", (p: Property) => formatPrice(p.precio)],
                    ["Superficie", (p: Property) => `${p.metros_cuadrados} m²`],
                    ["Parcela", (p: Property) => p.metros_parcela ? `${p.metros_parcela} m²` : "—"],
                    ["Habitaciones", (p: Property) => String(p.habitaciones)],
                    ["Baños", (p: Property) => String(p.banos)],
                    ["Garaje", (p: Property) => p.garaje ? `Sí (${p.plazas_garaje || 1})` : "No"],
                    ["Jardín", (p: Property) => p.jardin ? "Sí" : "No"],
                    ["Piscina", (p: Property) => p.piscina ? "Sí" : "No"],
                    ["Año", (p: Property) => p.ano_construccion ? String(p.ano_construccion) : "—"],
                    ["Estado", (p: Property) => p.estado_conservacion],
                  ] as [string, (p: Property) => string][]).map(([label, fn]) => (
                    <tr key={label} className="border-b">
                      <td className="py-2.5 px-2 text-muted-foreground">{label}</td>
                      {compareProperties.map((p) => (
                        <td key={p.id} className="py-2.5 px-2">{fn(p)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
